import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  X,
  Check,
  CheckCircle2,
  Zap,
  Shield,
  ArrowRight,
  ArrowLeft,
  Star,
  Car,
  Loader2,
  AlertCircle,
  XCircle,
  Tag,
  ClipboardCheck,
  ScanLine,
  User,
  Smartphone,
  Mail,
  MapPin,
  MessageCircle,
  Copy,
  KeyRound,
  Calendar,
  Clock,
  Search,
  RefreshCw,
  SkipForward,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  fetchInspectionPackages,
  isCrmConfigured,
  toErrorMessage,
  createOrder,
  verifyOrderWithRetry,
  sendOtp,
  verifyOtp,
  resendOtp,
  lookupVehicle,
  toE164India,
} from '../services/crmApi';
import { openRazorpay } from '../services/razorpay';
import { trackConversion, setEnhancedConversion } from '../utils/gtag';

/* ========================================================================= */
/* CONSTANTS                                                                 */
/* ========================================================================= */

const POPULAR_CITIES = [
  'Bengaluru',
  'Mumbai',
  'Delhi NCR',
  'Hyderabad',
  'Chennai',
  'Pune',
  'Kolkata',
  'Ahmedabad',
];

// 9 AM – 5 PM, hourly. CRM confirmed to hardcode until Phase 3.
const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00',
  '14:00', '15:00', '16:00', '17:00',
];

const RAZORPAY_THEME_COLOR = '#0a7d62';
const RAZORPAY_MERCHANT_NAME = 'Wisedrive';
const RAZORPAY_LOGO_URL = 'https://crm.wisedrive.in/static/logo.png';

const OTP_RESEND_COOLDOWN = 30; // seconds

const formatMoney = (amount, symbol = '₹') =>
  `${symbol}${Number(amount ?? 0).toLocaleString('en-IN')}`;

/* ========================================================================= */
/* MAIN COMPONENT                                                            */
/* ========================================================================= */

/**
 * BookingFlow — 9-state inspection booking flow backed by CRM Phase 2.1.
 *
 *   list     → packages grid (GET /packages)
 *   details  → selected package details
 *   customer → name, phone, (email) — triggers POST /otp/send
 *   otp      → 6-digit input — calls POST /otp/verify → gets JWT
 *   branch   → "Do you have your car details?" (Yes/Skip)
 *   vehicle  → reg-no input + lookup (GET /vehicles/lookup)
 *   schedule → city + date + time slot
 *   summary  → order review → POST /orders/create + Razorpay Checkout
 *   success  → paid confirmation (with booking_reference)
 *   failure  → retry / soft-failure / signature-fail
 */
const InspectionPackages = ({ isOpen, onClose, countryCode = 'IN' }) => {
  const [view, setView] = useState('list');

  // Packages
  const [packages, setPackages] = useState([]);
  const [country, setCountry] = useState(null);
  const [pkgsLoading, setPkgsLoading] = useState(false);
  const [pkgsError, setPkgsError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Customer form
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '' });

  // OTP
  const [otpState, setOtpState] = useState(null); // {request_id, expires_in_seconds, masked_phone}
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0); // resend timer
  const [otpLockUntil, setOtpLockUntil] = useState(0); // epoch ms, if 15-min lockout

  // Verification token (JWT) — memory only
  const [verificationToken, setVerificationToken] = useState('');

  // Vehicle
  const [regNo, setRegNo] = useState('');
  const [vehicle, setVehicle] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleError, setVehicleError] = useState('');

  // Schedule
  const [schedule, setSchedule] = useState({ date: '', time_slot: '', city: '' });

  // Payment
  const [paidOrder, setPaidOrder] = useState(null);
  const [failureInfo, setFailureInfo] = useState({});
  const [processing, setProcessing] = useState(false);

  // Reset everything on open
  useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setSelectedPackage(null);
    setCustomer({ name: '', phone: '', email: '' });
    setOtpState(null);
    setOtpInput('');
    setOtpError('');
    setOtpAttemptsLeft(null);
    setOtpCooldown(0);
    setOtpLockUntil(0);
    setVerificationToken('');
    setRegNo('');
    setVehicle(null);
    setVehicleError('');
    setSchedule({ date: '', time_slot: '', city: '' });
    setPaidOrder(null);
    setFailureInfo({});
  }, [isOpen]);

  // Load packages when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    async function load() {
      if (!isCrmConfigured()) {
        setPkgsError('CRM is not configured.');
        return;
      }
      setPkgsLoading(true);
      setPkgsError('');
      try {
        const { country: c, packages: pkgs } = await fetchInspectionPackages(countryCode);
        if (cancelled) return;
        setCountry(c);
        setPackages(pkgs);
      } catch (e) {
        if (cancelled) return;
        setPkgsError(toErrorMessage(e, 'Failed to load packages'));
      } finally {
        if (!cancelled) setPkgsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, countryCode]);

  // OTP resend cooldown timer
  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const t = setTimeout(() => setOtpCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  const symbol = country?.currency_symbol || '₹';

  /* ---- Flow transitions ------------------------------------------------- */

  const goToDetails = (pkg) => {
    setSelectedPackage(pkg);
    setView('details');
  };

  const goToCustomer = () => setView('customer');

  const handleSendOtp = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const phoneE164 = toE164India(customer.phone);
      const resp = await sendOtp({
        phone: phoneE164,
        country_code: (countryCode || 'IN').toUpperCase(),
        purpose: 'inspection_booking',
      });
      setOtpState({
        request_id: resp.request_id,
        masked_phone: resp.masked_phone,
        channel: resp.channel,
      });
      setOtpInput('');
      setOtpAttemptsLeft(null);
      setOtpCooldown(OTP_RESEND_COOLDOWN);
      setView('otp');
    } catch (e) {
      setOtpError(toErrorMessage(e, 'Could not send OTP'));
    } finally {
      setOtpSending(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpState) return;
    if (otpCooldown > 0) return;
    setOtpError('');
    try {
      await resendOtp({ request_id: otpState.request_id });
      setOtpCooldown(OTP_RESEND_COOLDOWN);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.retry_after_seconds) {
        setOtpCooldown(detail.retry_after_seconds);
      }
      setOtpError(toErrorMessage(e, 'Could not resend OTP'));
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpState || otpInput.length !== 6) return;
    setOtpVerifying(true);
    setOtpError('');
    try {
      const resp = await verifyOtp({
        request_id: otpState.request_id,
        otp: otpInput,
      });
      setVerificationToken(resp.verification_token);
      setView('branch');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.error;
      if (code === 'invalid_otp') {
        setOtpAttemptsLeft(detail?.attempts_left ?? 0);
        setOtpError(
          `Incorrect OTP. ${detail?.attempts_left ?? 0} attempt${
            (detail?.attempts_left ?? 0) === 1 ? '' : 's'
          } left.`
        );
        setOtpInput('');
      } else if (code === 'expired') {
        setOtpError('OTP expired. Please resend.');
        setOtpInput('');
      } else if (code === 'max_attempts_exceeded') {
        const retry = detail?.retry_after_seconds || 900;
        setOtpLockUntil(Date.now() + retry * 1000);
        setOtpError('Too many wrong attempts. Please try again in 15 minutes.');
      } else {
        setOtpError(toErrorMessage(e, 'Verification failed'));
      }
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleLookupVehicle = async () => {
    const trimmed = regNo.trim().toUpperCase();
    if (trimmed.length < 4) {
      setVehicleError('Enter a valid registration number.');
      return;
    }
    setVehicleLoading(true);
    setVehicleError('');
    try {
      const resp = await lookupVehicle({ reg_no: trimmed, country_code: 'IN' });
      if (resp?.success && resp.vehicle) {
        setVehicle({ ...resp.vehicle, reg_no: trimmed });
      } else {
        setVehicleError("We couldn't find that vehicle.");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = detail?.error;
      if (status === 404 || code === 'vehicle_not_found') {
        setVehicleError("We couldn't find that vehicle. Please check the number or skip to payment.");
      } else if (code === 'invalid_reg_no_format') {
        setVehicleError('Invalid registration number format.');
      } else if (status === 503 || code === 'rto_provider_unavailable') {
        setVehicleError('Vehicle lookup is temporarily unavailable. You can skip and continue.');
      } else if (status === 429) {
        setVehicleError('Too many lookups. Please wait a moment and try again.');
      } else {
        setVehicleError(toErrorMessage(e, 'Lookup failed'));
      }
    } finally {
      setVehicleLoading(false);
    }
  };

  const handleConfirmVehicle = () => {
    setSchedule((s) => ({ ...s, city: s.city || customer.city || POPULAR_CITIES[0] }));
    setView('schedule');
  };

  const handleSkipVehicle = () => {
    setVehicle(null);
    setSchedule({ date: '', time_slot: '', city: '' });
    setView('summary');
  };

  const goToSummary = () => setView('summary');

  /* ---- Razorpay payment orchestrator ------------------------------------ */

  const handlePay = useCallback(async () => {
    if (!selectedPackage || !verificationToken) {
      setFailureInfo({
        title: 'Session expired',
        message: 'Please verify your mobile number again.',
        recoverable: true,
        resetTo: 'customer',
      });
      setView('failure');
      return;
    }
    setProcessing(true);
    setFailureInfo({});

    const payload = {
      package_id: selectedPackage.id,
      country_code: (countryCode || 'IN').toUpperCase(),
      customer: {
        name: customer.name.trim(),
        phone: toE164India(customer.phone),
        ...(customer.email?.trim() ? { email: customer.email.trim() } : {}),
      },
      verification_token: verificationToken,
      ...(schedule.city ? { city: schedule.city } : {}),
      ...(vehicle
        ? {
            vehicle: {
              reg_no: vehicle.reg_no,
              make: vehicle.make,
              model: vehicle.model,
              fuel_type: vehicle.fuel_type,
              color: vehicle.color,
              registration_date: vehicle.registration_date,
              rto: vehicle.rto,
            },
          }
        : {}),
      ...(schedule.date && schedule.time_slot
        ? {
            schedule: {
              date: schedule.date,
              time_slot: schedule.time_slot,
              city: schedule.city,
            },
          }
        : {}),
      notes: { source: 'website' },
    };

    let orderResp;
    try {
      orderResp = await createOrder(payload);
    } catch (e) {
      const status = e?.response?.status;
      // 401 = token invalid/expired/used → back to OTP
      if (status === 401) {
        setVerificationToken('');
        setFailureInfo({
          title: 'Verification expired',
          message: 'Please verify your mobile number again to continue.',
          recoverable: true,
          resetTo: 'customer',
        });
      } else {
        setFailureInfo({
          title: "Couldn't start payment",
          message: toErrorMessage(e, 'Please try again in a moment.'),
          recoverable: true,
          resetTo: 'summary',
        });
      }
      setView('failure');
      setProcessing(false);
      return;
    }

    try {
      const rzp = await openRazorpay({
        key: orderResp.rzp_key_id,
        amount: orderResp.amount,
        currency: orderResp.currency || 'INR',
        name: RAZORPAY_MERCHANT_NAME,
        description: `${selectedPackage.name.trim()} · Inspection`,
        image: RAZORPAY_LOGO_URL,
        order_id: orderResp.rzp_order_id,
        prefill: {
          name: orderResp.customer?.name || customer.name,
          email: orderResp.customer?.email || customer.email || '',
          contact: orderResp.customer?.phone || toE164India(customer.phone),
        },
        notes: { order_id: orderResp.order_id },
        theme: { color: RAZORPAY_THEME_COLOR },
        handler: async (rzpResponse) => {
          try {
            const verifyResp = await verifyOrderWithRetry({
              order_id: orderResp.order_id,
              razorpay_order_id: rzpResponse.razorpay_order_id,
              razorpay_payment_id: rzpResponse.razorpay_payment_id,
              razorpay_signature: rzpResponse.razorpay_signature,
            });
            setPaidOrder(verifyResp.order || null);
            setView('success');
            // Google Ads — purchase conversion for ROAS bidding
            try {
              const paidOrderData = verifyResp.order || {};
              const amountRupees = paidOrderData.amount_paid != null
                ? Number(paidOrderData.amount_paid) / 100
                : (orderResp.amount != null ? Number(orderResp.amount) / 100 : undefined);
              setEnhancedConversion({
                phone: orderResp.customer?.phone || toE164India(customer.phone),
                email: orderResp.customer?.email || customer.email || undefined,
              });
              trackConversion('purchase', {
                value: amountRupees,
                currency: 'INR',
                transaction_id: paidOrderData.booking_reference
                  || paidOrderData.order_id
                  || orderResp.order_id,
              });
            } catch (_) { /* never break the success path */ }
          } catch (e) {
            const status = e?.response?.status;
            if (!status || status >= 500 || status === 408 || status === 429) {
              setFailureInfo({
                title: 'Payment received',
                message:
                  'Payment received. Our team will call you on WhatsApp within 30 minutes to schedule your inspection.',
                recoverable: false,
              });
            } else {
              setFailureInfo({
                title: 'Verification failed',
                message: toErrorMessage(e, 'Please contact support with your payment reference.'),
                recoverable: false,
              });
            }
            setView('failure');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            // stay on summary — user can retry Pay
          },
        },
      });

      rzp.on('payment.failed', (resp) => {
        setProcessing(false);
        setFailureInfo({
          title: 'Payment failed',
          message:
            resp?.error?.description ||
            'Your payment did not go through. Please try a different card or UPI.',
          recoverable: true,
          resetTo: 'summary',
        });
        setView('failure');
      });

      rzp.open();
    } catch (e) {
      setProcessing(false);
      setFailureInfo({
        title: "Couldn't open payment",
        message: toErrorMessage(e, 'Please check your connection and try again.'),
        recoverable: true,
        resetTo: 'summary',
      });
      setView('failure');
    }
  }, [selectedPackage, verificationToken, customer, vehicle, schedule, countryCode]);

  if (!isOpen) return null;

  /* ---- Header titles per view ------------------------------------------- */

  const headerConfig = {
    list: { title: 'Choose Your Inspection Package', subtitle: country?.name ? `Available in ${country.name}${packages.length ? ` · ${packages.length} options` : ''}` : '' },
    details: { title: selectedPackage?.name?.trim() || 'Package', subtitle: 'Selected plan', back: () => setView('list') },
    customer: { title: 'Your details', subtitle: 'Step 1 of 4', back: () => setView('details') },
    otp: { title: 'Verify mobile', subtitle: 'Step 2 of 4', back: () => setView('customer') },
    branch: { title: 'Car details', subtitle: 'Step 3 of 4 · optional', back: () => setView('otp') },
    vehicle: { title: 'Find your car', subtitle: 'Step 3 of 4', back: () => setView('branch') },
    schedule: { title: 'Schedule inspection', subtitle: 'Step 4 of 4', back: () => (vehicle ? setView('vehicle') : setView('branch')) },
    summary: { title: 'Review & pay', subtitle: 'Step 4 of 4', back: () => (vehicle ? setView('schedule') : setView('branch')) },
    success: { title: 'Booking confirmed', subtitle: 'Thank you!' },
    failure: { title: failureInfo.title || 'Payment', subtitle: '' },
  };

  const h = headerConfig[view] || {};

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-2 sm:p-4"
      data-testid="inspection-packages-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-3xl max-w-7xl w-full max-h-[96vh] overflow-y-auto relative shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-5 sm:px-6 py-4 rounded-t-3xl z-10 flex items-center gap-3">
          <button
            onClick={onClose}
            data-testid="inspection-packages-close"
            className="absolute top-3 right-3 h-10 w-10 rounded-full bg-white text-slate-900 hover:bg-slate-100 flex items-center justify-center shadow-lg ring-2 ring-white/40 transition-all"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2.5} />
          </button>
          {h.back ? (
            <button
              onClick={h.back}
              data-testid="flow-header-back"
              className="h-10 w-10 rounded-full bg-white/25 hover:bg-white/35 flex items-center justify-center transition-all flex-shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : (
            <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5 text-amber-300" />
            </div>
          )}
          <div className="min-w-0 pr-12">
            {h.subtitle && (
              <p className="text-blue-100 text-[10px] uppercase tracking-wide font-semibold">
                {h.subtitle}
              </p>
            )}
            <h2 className="text-lg sm:text-xl font-bold leading-tight truncate">{h.title}</h2>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {pkgsLoading && view === 'list' && <CenterLoader label="Loading packages…" />}
          {pkgsError && view === 'list' && <InlineError message={pkgsError} />}

          {!pkgsLoading && !pkgsError && view === 'list' && packages.length > 0 && (
            <ListView packages={packages} symbol={symbol} onSelect={goToDetails} />
          )}

          {!pkgsLoading && !pkgsError && view === 'list' && packages.length === 0 && (
            <div className="py-10 sm:py-14 text-center" data-testid="inspection-packages-empty">
              <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                Packages temporarily unavailable
              </h3>
              <p className="text-sm text-slate-600 max-w-sm mx-auto mb-5">
                Our pricing is being refreshed. Please WhatsApp us — we'll send a quote in 2 minutes.
              </p>
              <a
                href="https://wa.me/917353343536?text=Hi%2C%20I%20want%20to%20book%20an%20inspection%20but%20packages%20are%20not%20loading.%20Please%20share%20pricing."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="inspection-packages-empty-whatsapp"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607z"/>
                </svg>
                Chat on WhatsApp · +91 73533 43536
              </a>
            </div>
          )}

          {view === 'details' && selectedPackage && (
            <DetailsView pkg={selectedPackage} symbol={symbol} onContinue={goToCustomer} />
          )}

          {view === 'customer' && selectedPackage && (
            <CustomerView
              pkg={selectedPackage}
              symbol={symbol}
              customer={customer}
              setCustomer={setCustomer}
              error={otpError}
              sending={otpSending}
              onSubmit={handleSendOtp}
            />
          )}

          {view === 'otp' && selectedPackage && otpState && (
            <OtpView
              pkg={selectedPackage}
              symbol={symbol}
              otpState={otpState}
              otpInput={otpInput}
              setOtpInput={setOtpInput}
              error={otpError}
              attemptsLeft={otpAttemptsLeft}
              verifying={otpVerifying}
              cooldown={otpCooldown}
              lockUntil={otpLockUntil}
              onVerify={handleVerifyOtp}
              onResend={handleResendOtp}
              onChangePhone={() => setView('customer')}
            />
          )}

          {view === 'branch' && selectedPackage && (
            <BranchView
              onYes={() => setView('vehicle')}
              onSkip={handleSkipVehicle}
            />
          )}

          {view === 'vehicle' && selectedPackage && (
            <VehicleView
              regNo={regNo}
              setRegNo={setRegNo}
              vehicle={vehicle}
              loading={vehicleLoading}
              error={vehicleError}
              onLookup={handleLookupVehicle}
              onReset={() => {
                setVehicle(null);
                setVehicleError('');
                setRegNo('');
              }}
              onConfirm={handleConfirmVehicle}
              onSkip={handleSkipVehicle}
            />
          )}

          {view === 'schedule' && selectedPackage && (
            <ScheduleView
              schedule={schedule}
              setSchedule={setSchedule}
              onContinue={goToSummary}
            />
          )}

          {view === 'summary' && selectedPackage && (
            <SummaryView
              pkg={selectedPackage}
              symbol={symbol}
              customer={customer}
              vehicle={vehicle}
              schedule={schedule}
              processing={processing}
              onPay={handlePay}
            />
          )}

          {view === 'success' && (
            <SuccessView
              order={paidOrder}
              pkg={selectedPackage}
              symbol={symbol}
              onClose={onClose}
            />
          )}

          {view === 'failure' && (
            <FailureView
              info={failureInfo}
              onRetry={() => {
                const target = failureInfo.resetTo || 'summary';
                setFailureInfo({});
                setView(target);
              }}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ========================================================================= */
/* HELPERS                                                                   */
/* ========================================================================= */

function CenterLoader({ label }) {
  return (
    <div className="flex items-center justify-center py-16 text-slate-600" data-testid="flow-loader">
      <Loader2 className="h-6 w-6 animate-spin mr-3 text-blue-600" />
      {label}
    </div>
  );
}

function InlineError({ message }) {
  return (
    <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700" data-testid="flow-error">
      <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
      <p className="text-sm">{String(message)}</p>
    </div>
  );
}

function FormField({ label, required, optional, hint, error, icon, prefix, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
          {optional && <span className="text-slate-400 ml-1 normal-case font-normal">(optional)</span>}
        </label>
      </div>
      <div
        className={`flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-3 border transition-colors ${
          error ? 'border-red-400 bg-red-50' : 'border-slate-200 focus-within:border-blue-500 focus-within:bg-white'
        }`}
      >
        {icon && <span className="text-slate-500 flex-shrink-0">{icon}</span>}
        {prefix}
        {children}
      </div>
      {hint && !error && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

/* ========================================================================= */
/* LIST VIEW                                                                 */
/* ========================================================================= */

function ListView({ packages, symbol, onSelect }) {
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5" data-testid="inspection-packages-grid">
        {packages.map((pkg) => {
          const isRec = !!pkg.is_recommended;
          const pkgSymbol = pkg.currency_symbol || symbol;
          const categories = pkg.category_details || [];
          const topCategories = categories
            .filter((c) => (c.check_points ?? 0) > 0)
            .sort((a, b) => (b.check_points ?? 0) - (a.check_points ?? 0))
            .slice(0, 3);
          let offerLabel = null;
          if (pkg.allow_discount && pkg.discount_value > 0) {
            offerLabel =
              pkg.discount_type === 'percentage'
                ? `${pkg.discount_value}% off`
                : `Flat ${formatMoney(pkg.discount_value, pkgSymbol)} off`;
          }
          return (
            <div
              key={pkg.id}
              data-testid={`package-card-${pkg.id}`}
              className={`relative rounded-2xl p-5 sm:p-4 flex flex-col transition-all duration-200 ${
                isRec
                  ? 'bg-white border-2 border-blue-500 shadow-md hover:shadow-lg'
                  : 'bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-lg sm:text-base font-bold text-slate-900 leading-tight">
                  {pkg.name.trim()}
                </h3>
                {isRec && (
                  <span className="flex items-center gap-1 bg-blue-600 text-white text-[10px] sm:text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 uppercase tracking-wide">
                    <Star className="h-3 w-3 sm:h-2.5 sm:w-2.5 fill-white" />
                    Popular
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1.5 mb-3 sm:mb-2">
                <span className="text-3xl sm:text-2xl font-extrabold text-slate-900">
                  {formatMoney(pkg.price, pkgSymbol)}
                </span>
                <span className="text-xs sm:text-[11px] text-slate-500">incl. taxes</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm sm:text-[11px] text-slate-600 mb-3 sm:mb-2">
                <span className="flex items-center gap-1">
                  <ClipboardCheck className="h-4 w-4 sm:h-3 sm:w-3 text-blue-600" />
                  <span className="font-semibold text-slate-900">{pkg.total_check_points}</span>
                  <span>points</span>
                </span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="flex items-center gap-1">
                  <Car className="h-4 w-4 sm:h-3 sm:w-3 text-blue-600" />
                  <span className="font-semibold text-slate-900">{pkg.no_of_inspections}</span>
                  <span>{pkg.no_of_inspections > 1 ? 'cars' : 'car'}</span>
                </span>
              </div>
              {topCategories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 sm:gap-1 mb-3">
                  {topCategories.map((c) => (
                    <span key={c.id} className="text-xs sm:text-[10px] bg-blue-50 text-blue-700 border border-blue-100 px-2 sm:px-1.5 py-1 sm:py-0.5 rounded-md">
                      {c.name}
                    </span>
                  ))}
                </div>
              )}
              {offerLabel && (
                <div className="mb-3 inline-flex items-center gap-1 text-sm sm:text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 sm:px-2 py-1 sm:py-0.5 self-start">
                  <Tag className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                  {offerLabel}
                </div>
              )}
              <Button
                onClick={() => onSelect(pkg)}
                data-testid={`package-select-${pkg.id}`}
                className={`mt-auto w-full rounded-xl h-12 sm:h-9 text-sm sm:text-xs font-semibold ${
                  isRec ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                View details
                <ArrowRight className="ml-1.5 h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </Button>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ========================================================================= */
/* DETAILS VIEW                                                              */
/* ========================================================================= */

function DetailsView({ pkg, symbol, onContinue }) {
  const pkgSymbol = pkg.currency_symbol || symbol;
  const categories = useMemo(
    () => [...(pkg.category_details || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [pkg.category_details]
  );
  return (
    <div data-testid="package-details-view">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-3xl p-6 mb-5">
        <div className="grid md:grid-cols-3 gap-4 items-center">
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold mb-1">{pkg.name.trim()}</h3>
            <p className="text-blue-100 text-sm">
              {pkg.description || 'Comprehensive vehicle inspection.'}
            </p>
          </div>
          <div className="md:text-right">
            <div className="text-blue-100 text-xs uppercase tracking-wide font-semibold">Total price</div>
            <div className="text-4xl font-black leading-none mt-1">{formatMoney(pkg.price, pkgSymbol)}</div>
            <div className="text-blue-100 text-xs mt-1">incl. all taxes</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Check points" value={pkg.total_check_points} icon={<ClipboardCheck className="h-5 w-5 text-blue-600" />} />
        <StatCard label="Inspections" value={pkg.no_of_inspections} icon={<Car className="h-5 w-5 text-blue-600" />} />
        <StatCard label="Categories" value={categories.length} icon={<ScanLine className="h-5 w-5 text-blue-600" />} />
        <StatCard label="Brands" value={pkg.brands_covered?.length || 0} icon={<Star className="h-5 w-5 text-blue-600" />} />
      </div>

      <div className="mb-5">
        <h4 className="text-base font-bold text-slate-900 mb-2">What we inspect</h4>
        <div className="grid md:grid-cols-2 gap-2">
          {categories.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-2">
              <ClipboardCheck className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <span className="text-sm text-slate-800 truncate">{c.name}</span>
                {c.check_points > 0 ? (
                  <span className="text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {c.check_points} pts
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    Included
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 bg-white pt-4 border-t border-slate-100">
        <Button
          onClick={onContinue}
          data-testid="detail-continue-btn"
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-5 rounded-xl font-semibold text-base shadow-lg"
        >
          Continue — {formatMoney(pkg.price, pkgSymbol)}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2">
      <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
        <div className="text-lg font-bold text-slate-900 leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* CUSTOMER VIEW                                                             */
/* ========================================================================= */

function CustomerView({ pkg, symbol, customer, setCustomer, error, sending, onSubmit }) {
  const pkgSymbol = pkg.currency_symbol || symbol;
  const [errors, setErrors] = useState({});

  const setField = (k, transform) => (e) => {
    const raw = e?.target ? e.target.value : e;
    const v = transform ? transform(raw) : raw;
    setCustomer((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!customer.name.trim() || customer.name.trim().length < 2) errs.name = 'Please enter your full name';
    const phoneDigits = (customer.phone || '').replace(/\D/g, '');
    if (phoneDigits.length !== 10) errs.phone = 'Enter a valid 10-digit mobile number';
    if (customer.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) errs.email = 'Enter a valid email';
    setErrors(errs);
    if (Object.keys(errs).length === 0) onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} data-testid="customer-form" className="max-w-xl mx-auto">
      <PackageSummary pkg={pkg} symbol={pkgSymbol} />
      <div className="space-y-4 mb-5">
        <FormField label="Full name" required error={errors.name} icon={<User className="h-4 w-4" />}>
          <input
            data-testid="form-name"
            autoFocus
            type="text"
            value={customer.name}
            onChange={setField('name')}
            placeholder="Ravi Kumar"
            autoComplete="name"
            className="w-full bg-transparent outline-none text-sm sm:text-base text-slate-900 placeholder-slate-400"
          />
        </FormField>
        <FormField
          label="Mobile number"
          required
          hint="We'll send a 6-digit OTP to verify"
          error={errors.phone}
          icon={<Smartphone className="h-4 w-4" />}
          prefix={<span className="text-slate-500 text-sm font-medium mr-1">+91</span>}
        >
          <input
            data-testid="form-phone"
            type="tel"
            inputMode="numeric"
            value={customer.phone}
            onChange={setField('phone', (v) => v.replace(/\D/g, '').slice(0, 10))}
            placeholder="9876543210"
            autoComplete="tel"
            maxLength={10}
            className="w-full bg-transparent outline-none text-sm sm:text-base text-slate-900 placeholder-slate-400"
          />
        </FormField>
        <FormField label="Email" optional error={errors.email} icon={<Mail className="h-4 w-4" />}>
          <input
            data-testid="form-email"
            type="email"
            value={customer.email}
            onChange={setField('email')}
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full bg-transparent outline-none text-sm sm:text-base text-slate-900 placeholder-slate-400"
          />
        </FormField>
      </div>
      {error && <InlineError message={error} />}
      <div className="sticky bottom-0 bg-white pt-4 border-t border-slate-100 mt-5">
        <Button
          type="submit"
          data-testid="customer-send-otp-btn"
          disabled={sending}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-5 rounded-xl font-semibold disabled:opacity-60"
        >
          {sending ? (
            <><Loader2 className="h-5 w-5 animate-spin mr-2" />Sending OTP…</>
          ) : (
            <>Send OTP <ArrowRight className="ml-2 h-5 w-5" /></>
          )}
        </Button>
      </div>
    </form>
  );
}

function PackageSummary({ pkg, symbol }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Booking</p>
        <p className="text-base font-bold text-slate-900 truncate">{pkg.name.trim()}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {pkg.total_check_points} check points · {pkg.no_of_inspections}{' '}
          {pkg.no_of_inspections > 1 ? 'cars' : 'car'}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-2xl font-extrabold text-slate-900 leading-none">
          {formatMoney(pkg.price, symbol)}
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">incl. taxes</div>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* OTP VIEW                                                                  */
/* ========================================================================= */

function OtpView({
  pkg, symbol, otpState, otpInput, setOtpInput, error, attemptsLeft, verifying,
  cooldown, lockUntil, onVerify, onResend, onChangePhone,
}) {
  const pkgSymbol = pkg.currency_symbol || symbol;
  const inputRef = useRef(null);
  const locked = lockUntil && Date.now() < lockUntil;
  const lockMinsLeft = locked ? Math.ceil((lockUntil - Date.now()) / 60000) : 0;

  useEffect(() => {
    if (inputRef.current && !locked) inputRef.current.focus();
  }, [locked]);

  useEffect(() => {
    if (otpInput.length === 6 && !verifying && !locked) {
      onVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpInput]);

  return (
    <div className="max-w-md mx-auto" data-testid="otp-view">
      <PackageSummary pkg={pkg} symbol={pkgSymbol} />

      <div className="text-center mb-5">
        <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
          <KeyRound className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-900">Enter the 6-digit OTP</h3>
        <p className="text-sm text-slate-600 mt-1">
          Sent to <span className="font-semibold">{otpState.masked_phone}</span>
          {otpState.channel === 'sms' && ' via SMS'}
        </p>
        <button
          type="button"
          onClick={onChangePhone}
          data-testid="otp-change-phone"
          className="text-xs text-blue-600 hover:text-blue-700 font-semibold mt-1 underline underline-offset-2"
        >
          Change number
        </button>
      </div>

      <div className="mb-4">
        <input
          ref={inputRef}
          data-testid="otp-input"
          type="tel"
          inputMode="numeric"
          maxLength={6}
          value={otpInput}
          onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
          disabled={verifying || locked}
          placeholder="• • • • • •"
          className={`w-full text-center text-3xl sm:text-4xl font-bold tracking-[0.5em] py-4 rounded-xl border-2 bg-slate-50 outline-none transition-colors ${
            error ? 'border-red-400 bg-red-50 text-red-700' : 'border-slate-200 focus:border-blue-500 focus:bg-white'
          } ${locked ? 'opacity-50' : ''}`}
          autoComplete="one-time-code"
        />
        {error && !locked && (
          <p className="text-xs text-red-600 mt-2 text-center" data-testid="otp-error">
            {error}
            {attemptsLeft !== null && attemptsLeft > 0 && ` (${attemptsLeft} left)`}
          </p>
        )}
        {locked && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-center" data-testid="otp-lockout">
            <p className="text-sm font-semibold text-red-700">Too many wrong attempts</p>
            <p className="text-xs text-red-600 mt-1">
              Try again in {lockMinsLeft} minute{lockMinsLeft === 1 ? '' : 's'}.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mb-5">
        {cooldown > 0 ? (
          <>
            <Clock className="h-4 w-4" />
            <span data-testid="otp-cooldown">Resend in {cooldown}s</span>
          </>
        ) : (
          <button
            type="button"
            onClick={onResend}
            data-testid="otp-resend"
            disabled={locked}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" /> Resend OTP
          </button>
        )}
      </div>

      <Button
        type="button"
        onClick={onVerify}
        data-testid="otp-verify-btn"
        disabled={otpInput.length !== 6 || verifying || locked}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-5 rounded-xl font-semibold disabled:opacity-60"
      >
        {verifying ? (
          <><Loader2 className="h-5 w-5 animate-spin mr-2" />Verifying…</>
        ) : (
          <>Verify & Continue <ArrowRight className="ml-2 h-5 w-5" /></>
        )}
      </Button>
    </div>
  );
}

/* ========================================================================= */
/* BRANCH VIEW                                                               */
/* ========================================================================= */

function BranchView({ onYes, onSkip }) {
  return (
    <div className="max-w-lg mx-auto py-4" data-testid="branch-view">
      <div className="text-center mb-6">
        <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
          <Car className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-900">Do you have your car's details?</h3>
        <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
          If you share the registration number now, we'll fetch your car and schedule the
          inspection instantly. Otherwise, our team will call you on WhatsApp to collect these.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onYes}
          data-testid="branch-yes-btn"
          className="group bg-white border-2 border-blue-600 rounded-2xl p-5 text-left hover:shadow-lg hover:bg-blue-50 transition-all"
        >
          <div className="h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center mb-3">
            <Search className="h-5 w-5" />
          </div>
          <h4 className="font-bold text-slate-900 mb-1">Yes, I have it</h4>
          <p className="text-xs text-slate-600">Enter reg number → we'll auto-fetch + schedule</p>
          <div className="mt-3 flex items-center text-blue-600 text-sm font-semibold">
            Continue <ArrowRight className="ml-1.5 h-4 w-4" />
          </div>
        </button>
        <button
          type="button"
          onClick={onSkip}
          data-testid="branch-skip-btn"
          className="group bg-white border-2 border-slate-200 rounded-2xl p-5 text-left hover:border-slate-400 hover:shadow-md transition-all"
        >
          <div className="h-10 w-10 rounded-xl bg-slate-200 text-slate-700 flex items-center justify-center mb-3">
            <SkipForward className="h-5 w-5" />
          </div>
          <h4 className="font-bold text-slate-900 mb-1">Skip, I'll share later</h4>
          <p className="text-xs text-slate-600">Pay now, our team calls on WhatsApp within 30 min</p>
          <div className="mt-3 flex items-center text-slate-700 text-sm font-semibold">
            Skip to payment <ArrowRight className="ml-1.5 h-4 w-4" />
          </div>
        </button>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* VEHICLE VIEW                                                              */
/* ========================================================================= */

function VehicleView({ regNo, setRegNo, vehicle, loading, error, onLookup, onReset, onConfirm, onSkip }) {
  return (
    <div className="max-w-lg mx-auto py-2" data-testid="vehicle-view">
      {!vehicle ? (
        <>
          <div className="text-center mb-5">
            <div className="h-14 w-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
              <Search className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Enter your registration number</h3>
            <p className="text-sm text-slate-600 mt-1">We'll fetch your car's details automatically.</p>
          </div>
          <div className="mb-4">
            <FormField label="Registration number" required icon={<Car className="h-4 w-4" />} error={error}>
              <input
                data-testid="vehicle-reg-input"
                type="text"
                value={regNo}
                onChange={(e) => setRegNo(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onLookup(); } }}
                placeholder="KA01AB1234"
                autoCapitalize="characters"
                autoCorrect="off"
                maxLength={12}
                className="w-full bg-transparent outline-none text-base font-mono font-semibold uppercase text-slate-900 placeholder-slate-400 tracking-wide"
              />
            </FormField>
          </div>
          <div className="space-y-2">
            <Button
              onClick={onLookup}
              data-testid="vehicle-lookup-btn"
              disabled={loading || !regNo.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-xl font-semibold disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="h-5 w-5 animate-spin mr-2" />Fetching…</>
              ) : (
                <>Fetch car details <Search className="ml-2 h-4 w-4" /></>
              )}
            </Button>
            <button
              type="button"
              onClick={onSkip}
              data-testid="vehicle-skip-btn"
              className="w-full text-sm text-slate-500 hover:text-slate-700 py-2"
            >
              Skip to payment
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-center mb-4">
            <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Is this your car?</h3>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-5 mb-5" data-testid="vehicle-card">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-blue-700 font-bold bg-white px-2 py-0.5 rounded">
                Reg No
              </span>
              <span className="font-mono font-bold text-slate-900">{vehicle.reg_no}</span>
            </div>
            <h4 className="text-lg font-bold text-slate-900 leading-tight mb-3">
              {vehicle.make} {vehicle.model}
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {vehicle.fuel_type && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Fuel</div>
                  <div className="font-semibold text-slate-900">{vehicle.fuel_type}</div>
                </div>
              )}
              {vehicle.color && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Colour</div>
                  <div className="font-semibold text-slate-900">{vehicle.color}</div>
                </div>
              )}
              {vehicle.registration_date && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Reg date</div>
                  <div className="font-semibold text-slate-900">{vehicle.registration_date}</div>
                </div>
              )}
              {vehicle.rto && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">RTO</div>
                  <div className="font-semibold text-slate-900 text-xs">{vehicle.rto}</div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onReset}
              data-testid="vehicle-change-btn"
              className="sm:w-auto py-5 rounded-xl"
            >
              Not my car
            </Button>
            <Button
              onClick={onConfirm}
              data-testid="vehicle-confirm-btn"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-5 rounded-xl font-semibold"
            >
              Yes, continue <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ========================================================================= */
/* SCHEDULE VIEW                                                             */
/* ========================================================================= */

function ScheduleView({ schedule, setSchedule, onContinue }) {
  // Generate next 7 days starting tomorrow
  const days = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 1; i <= 7; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      arr.push({
        iso: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString('en-IN', { weekday: 'short' }),
        day: d.getDate(),
        month: d.toLocaleDateString('en-IN', { month: 'short' }),
      });
    }
    return arr;
  }, []);

  const canContinue = schedule.city && schedule.date && schedule.time_slot;

  return (
    <div className="max-w-2xl mx-auto" data-testid="schedule-view">
      {/* City */}
      <div className="mb-5">
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" /> City
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {POPULAR_CITIES.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`schedule-city-${c.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setSchedule((s) => ({ ...s, city: c }))}
              className={`text-sm px-3 py-2.5 rounded-xl border-2 transition-all font-medium ${
                schedule.city === c
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <input
          data-testid="schedule-city-input"
          type="text"
          value={schedule.city}
          onChange={(e) => setSchedule((s) => ({ ...s, city: e.target.value }))}
          placeholder="Or type your city"
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 focus:bg-white text-sm"
        />
      </div>

      {/* Date */}
      <div className="mb-5">
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" /> Preferred date
        </label>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="schedule-date-grid">
          {days.map((d) => (
            <button
              key={d.iso}
              type="button"
              data-testid={`schedule-date-${d.iso}`}
              onClick={() => setSchedule((s) => ({ ...s, date: d.iso }))}
              className={`rounded-xl border-2 py-2.5 transition-all ${
                schedule.date === d.iso
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-slate-200 hover:border-blue-300'
              }`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                {d.label}
              </div>
              <div className={`text-xl font-bold leading-none mt-0.5`}>{d.day}</div>
              <div className="text-[10px] mt-0.5 opacity-80">{d.month}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Time */}
      <div className="mb-5">
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" /> Time slot
        </label>
        <div className="grid grid-cols-4 gap-2" data-testid="schedule-slot-grid">
          {TIME_SLOTS.map((t) => {
            const [hh, mm] = t.split(':').map(Number);
            const label12 = `${((hh + 11) % 12) + 1}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
            return (
              <button
                key={t}
                type="button"
                data-testid={`schedule-slot-${t}`}
                onClick={() => setSchedule((s) => ({ ...s, time_slot: t }))}
                className={`rounded-xl border-2 py-2.5 text-sm font-semibold transition-all ${
                  schedule.time_slot === t
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300'
                }`}
              >
                {label12}
              </button>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-0 bg-white pt-4 border-t border-slate-100">
        <Button
          onClick={onContinue}
          data-testid="schedule-continue-btn"
          disabled={!canContinue}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-5 rounded-xl font-semibold disabled:opacity-60"
        >
          Continue to review <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* SUMMARY VIEW                                                              */
/* ========================================================================= */

function SummaryView({ pkg, symbol, customer, vehicle, schedule, processing, onPay }) {
  const pkgSymbol = pkg.currency_symbol || symbol;
  return (
    <div className="max-w-xl mx-auto" data-testid="summary-view">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-blue-700 font-bold">Package</div>
            <div className="text-lg font-bold text-slate-900">{pkg.name.trim()}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold text-slate-900">{formatMoney(pkg.price, pkgSymbol)}</div>
            <div className="text-[10px] text-slate-500">incl. taxes</div>
          </div>
        </div>
        <div className="text-xs text-slate-600">
          {pkg.total_check_points} check points · {pkg.no_of_inspections}{' '}
          {pkg.no_of_inspections > 1 ? 'cars' : 'car'}
        </div>
      </div>

      <div className="space-y-2 mb-5">
        <SummaryRow label="Name" value={customer.name} />
        <SummaryRow label="Phone" value={toE164India(customer.phone)} />
        {customer.email && <SummaryRow label="Email" value={customer.email} />}
        {vehicle && (
          <SummaryRow
            label="Vehicle"
            value={`${vehicle.make} ${vehicle.model} · ${vehicle.reg_no}`}
          />
        )}
        {schedule.city && <SummaryRow label="City" value={schedule.city} />}
        {schedule.date && schedule.time_slot && (
          <SummaryRow label="Slot" value={`${schedule.date} · ${schedule.time_slot}`} />
        )}
      </div>

      {!vehicle && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-xs text-amber-900">
          <MessageCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Our team will call you on WhatsApp within 30 minutes to collect your car details
            and schedule the inspection.
          </span>
        </div>
      )}

      <div className="sticky bottom-0 bg-white pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-3">
          <Shield className="h-3.5 w-3.5 text-blue-600" />
          Payment secured by Razorpay · 256-bit encrypted
        </div>
        <Button
          onClick={onPay}
          data-testid="summary-pay-btn"
          disabled={processing}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-5 rounded-xl font-semibold text-base shadow-lg disabled:opacity-60"
        >
          {processing ? (
            <><Loader2 className="h-5 w-5 animate-spin mr-2" />Opening payment…</>
          ) : (
            <>Pay {formatMoney(pkg.price, pkgSymbol)} <ArrowRight className="ml-2 h-5 w-5" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900 truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}

/* ========================================================================= */
/* SUCCESS VIEW                                                              */
/* ========================================================================= */

function SuccessView({ order, pkg, symbol, onClose }) {
  const pkgSymbol = order?.currency_symbol || pkg?.currency_symbol || symbol;
  const reference = order?.booking_reference || (order?.order_id ? order.order_id.slice(0, 8).toUpperCase() : 'pending');
  const [copied, setCopied] = useState(false);

  const copyRef = () => {
    if (!reference) return;
    navigator.clipboard?.writeText(reference).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }, () => {});
  };

  const amount = order?.amount ?? pkg?.price ?? 0;
  const packageName = order?.package?.name || pkg?.name || 'Inspection';
  const status = order?.inspection_status;
  const scheduled = order?.schedule;
  const vehicle = order?.vehicle;

  let nextStepMsg;
  if (status === 'SCHEDULED' && scheduled?.date && scheduled?.time_slot) {
    nextStepMsg = `Scheduled for ${scheduled.date} at ${scheduled.time_slot}${scheduled.city ? ' · ' + scheduled.city : ''}. Our inspector will call 30 min before arrival.`;
  } else if (status === 'NEW_INSPECTION' && vehicle) {
    nextStepMsg = `Payment received for your ${vehicle.make} ${vehicle.model}. Our team will call you on WhatsApp within 30 minutes to schedule the inspection.`;
  } else {
    nextStepMsg = 'Payment received. Our team will call you on WhatsApp within 30 minutes to collect your car details and schedule.';
  }

  return (
    <div className="max-w-xl mx-auto py-4" data-testid="success-view">
      <div className="flex flex-col items-center text-center mb-5">
        <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-600" strokeWidth={2.2} />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-1">Payment successful</h3>
        <p className="text-sm text-slate-600 max-w-sm">
          Your {packageName.trim()} inspection is booked.
        </p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">
            Booking reference
          </span>
          <button
            type="button"
            onClick={copyRef}
            data-testid="success-copy-ref"
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="text-xl font-mono font-bold text-slate-900" data-testid="success-reference">
          {reference}
        </p>

        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-200 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Package</div>
            <div className="font-semibold text-slate-900 truncate">{packageName.trim()}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Amount paid</div>
            <div className="font-semibold text-slate-900">{formatMoney(amount, pkgSymbol)}</div>
          </div>
          {vehicle && (
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Vehicle</div>
              <div className="font-semibold text-slate-900 text-xs">
                {vehicle.make} {vehicle.model} · {vehicle.reg_no}
              </div>
            </div>
          )}
          {scheduled?.date && (
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Scheduled</div>
              <div className="font-semibold text-slate-900 text-xs">
                {scheduled.date} · {scheduled.time_slot} · {scheduled.city}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-4 flex items-start gap-3">
        <MessageCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <p className="font-semibold">What happens next?</p>
          <p className="mt-0.5 text-emerald-800">{nextStepMsg}</p>
        </div>
      </div>

      <Button
        onClick={onClose}
        data-testid="success-done-btn"
        className="w-full bg-slate-900 hover:bg-slate-800 text-white py-5 rounded-xl font-semibold"
      >
        Done
      </Button>
    </div>
  );
}

/* ========================================================================= */
/* FAILURE VIEW                                                              */
/* ========================================================================= */

function FailureView({ info, onRetry, onClose }) {
  const { title, message, recoverable } = info || {};
  const isSoft = !recoverable && /received/i.test(title || '');
  const Icon = isSoft ? CheckCircle2 : XCircle;
  const iconBg = isSoft ? 'bg-emerald-100' : 'bg-red-100';
  const iconColor = isSoft ? 'text-emerald-600' : 'text-red-600';

  return (
    <div className="max-w-xl mx-auto py-6" data-testid="failure-view">
      <div className="flex flex-col items-center text-center mb-5">
        <div className={`h-20 w-20 rounded-full ${iconBg} flex items-center justify-center mb-4`}>
          <Icon className={`h-12 w-12 ${iconColor}`} strokeWidth={2.2} />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">{title || 'Something went wrong'}</h3>
        <p className="text-sm text-slate-600 max-w-sm">
          {message || 'Please try again or contact us on WhatsApp.'}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        {recoverable && (
          <Button
            onClick={onRetry}
            data-testid="failure-retry-btn"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-xl font-semibold"
          >
            Try again
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onClose}
          data-testid="failure-close-btn"
          className="sm:w-auto py-5 rounded-xl"
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export default InspectionPackages;
